import {
  Alert,
  Badge,
  Box,
  Button,
  Center,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { Pencil2Icon, PlusIcon, TrashIcon } from "@radix-ui/react-icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormEvent, useState } from "react";
import { AdminStation, StationAdmin } from "use-smart-locks-shared";
import {
  demoteStationAdmin,
  getApiUserErrorMessage,
  getStationAdmins,
  promoteStationAdmin,
  updateStationAdminStations,
} from "../shared/api/nest-api";
import { useAdminContext } from "./admin-context";

type EditorState =
  | { mode: "create" }
  | { mode: "edit"; stationAdmin: StationAdmin };

export function StationAdminsPage() {
  const { stations } = useAdminContext();
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [adminToDemote, setAdminToDemote] = useState<StationAdmin | null>(null);
  const queryClient = useQueryClient();
  const toast = useToast();
  const adminsQuery = useQuery({
    queryKey: ["admin", "admins"],
    queryFn: getStationAdmins,
  });

  const refreshAdmins = async () => {
    await queryClient.invalidateQueries({ queryKey: ["admin", "admins"] });
  };

  const createMutation = useMutation({
    mutationFn: promoteStationAdmin,
    onSuccess: async (stationAdmin) => {
      await refreshAdmins();
      setEditor(null);
      toast({
        title: `${stationAdmin.email} ist jetzt Stationsadmin.`,
        status: "success",
        duration: 5000,
      });
    },
  });
  const updateMutation = useMutation({
    mutationFn: ({
      userId,
      stationIds,
    }: {
      userId: string;
      stationIds: string[];
    }) => updateStationAdminStations(userId, { stationIds }),
    onSuccess: async () => {
      await refreshAdmins();
      setEditor(null);
      toast({
        title: "Stationszugriff aktualisiert.",
        status: "success",
        duration: 5000,
      });
    },
  });
  const demoteMutation = useMutation({
    mutationFn: demoteStationAdmin,
    onSuccess: async () => {
      await refreshAdmins();
      setAdminToDemote(null);
      toast({
        title: "Adminrolle entfernt.",
        status: "success",
        duration: 5000,
      });
    },
  });

  const editorError = createMutation.error ?? updateMutation.error;
  const demoteError = demoteMutation.error;

  return (
    <Stack spacing="8">
      <Flex
        align={{ base: "flex-start", md: "center" }}
        justify="space-between"
        direction={{ base: "column", md: "row" }}
        gap="4"
      >
        <Box>
          <Heading size="xl">Administratoren</Heading>
          <Text color="gray.600" mt="2" maxW="2xl">
            Bestehende, verifizierte Konten zu Stationsadmins ernennen und ihren
            Zugriff präzise zuweisen.
          </Text>
        </Box>
        <Button
          leftIcon={<PlusIcon />}
          colorScheme="blue"
          onClick={() => setEditor({ mode: "create" })}
        >
          Admin hinzufügen
        </Button>
      </Flex>

      {adminsQuery.isPending && (
        <HStack spacing="3" color="gray.600" py="4" aria-live="polite">
          <Spinner size="sm" />
          <Text>Administratoren werden geladen …</Text>
        </HStack>
      )}

      {adminsQuery.error && (
        <Alert
          status="error"
          role="alert"
          alignItems="flex-start"
          flexDirection="column"
          gap="2"
        >
          <Text fontWeight="semibold">
            Administratoren konnten nicht geladen werden.
          </Text>
          <Text>
            {getApiUserErrorMessage(adminsQuery.error) ??
              "Bitte versuchen Sie es erneut."}
          </Text>
          <Button
            size="sm"
            variant="outline"
            onClick={() => void adminsQuery.refetch()}
          >
            Erneut versuchen
          </Button>
        </Alert>
      )}

      {adminsQuery.data?.length === 0 && (
        <Center
          minH="240px"
          bg="white"
          borderWidth="1px"
          borderRadius="lg"
          px="6"
        >
          <Stack align="center" spacing="2" textAlign="center">
            <Heading size="md">Noch keine Stationsadmins</Heading>
            <Text color="gray.600" maxW="lg">
              Ernennen Sie ein bestehendes Konto und wählen Sie mindestens eine
              Station aus.
            </Text>
          </Stack>
        </Center>
      )}

      {adminsQuery.data && adminsQuery.data.length > 0 && (
        <TableContainer
          bg="white"
          borderWidth="1px"
          borderRadius="lg"
          boxShadow="sm"
        >
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Konto</Th>
                <Th>Stationen</Th>
                <Th textAlign="right">Aktionen</Th>
              </Tr>
            </Thead>
            <Tbody>
              {adminsQuery.data.map((stationAdmin) => (
                <Tr key={stationAdmin.id}>
                  <Td>
                    <Text fontWeight="semibold">{stationAdmin.email}</Text>
                    <Text color="gray.500" fontSize="sm">
                      Stationsadmin
                    </Text>
                  </Td>
                  <Td>
                    <HStack spacing="2" flexWrap="wrap">
                      {stationAdmin.stations.map((station) => (
                        <Badge key={station.id} colorScheme="blue">
                          {station.name}
                        </Badge>
                      ))}
                    </HStack>
                  </Td>
                  <Td>
                    <HStack justify="flex-end" spacing="2">
                      <Button
                        size="sm"
                        leftIcon={<Pencil2Icon />}
                        variant="outline"
                        aria-label={`Stationen für ${stationAdmin.email} bearbeiten`}
                        onClick={() =>
                          setEditor({ mode: "edit", stationAdmin })
                        }
                      >
                        Bearbeiten
                      </Button>
                      <Button
                        size="sm"
                        leftIcon={<TrashIcon />}
                        colorScheme="red"
                        variant="ghost"
                        aria-label={`${stationAdmin.email} als Admin entfernen`}
                        onClick={() => setAdminToDemote(stationAdmin)}
                      >
                        Entfernen
                      </Button>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      {editor && (
        <AdminEditorModal
          key={
            editor.mode === "create"
              ? "create"
              : `edit-${editor.stationAdmin.id}`
          }
          editor={editor}
          stations={stations}
          isSubmitting={createMutation.isPending || updateMutation.isPending}
          error={
            editorError
              ? (getApiUserErrorMessage(editorError) ??
                "Die Änderung konnte nicht gespeichert werden.")
              : undefined
          }
          onClose={() => {
            createMutation.reset();
            updateMutation.reset();
            setEditor(null);
          }}
          onSubmit={async ({ email, stationIds }) => {
            if (editor.mode === "create") {
              await createMutation.mutateAsync({
                email: email ?? "",
                stationIds,
              });
            } else {
              await updateMutation.mutateAsync({
                userId: editor.stationAdmin.id,
                stationIds,
              });
            }
          }}
        />
      )}

      {adminToDemote && (
        <ConfirmDemotionModal
          stationAdmin={adminToDemote}
          isSubmitting={demoteMutation.isPending}
          error={
            demoteError
              ? (getApiUserErrorMessage(demoteError) ??
                "Die Adminrolle konnte nicht entfernt werden.")
              : undefined
          }
          onClose={() => {
            demoteMutation.reset();
            setAdminToDemote(null);
          }}
          onConfirm={() => demoteMutation.mutateAsync(adminToDemote.id)}
        />
      )}
    </Stack>
  );
}

function AdminEditorModal({
  editor,
  stations,
  isSubmitting,
  error,
  onClose,
  onSubmit,
}: {
  editor: EditorState;
  stations: AdminStation[];
  isSubmitting: boolean;
  error?: string;
  onClose: () => void;
  onSubmit: (data: { email?: string; stationIds: string[] }) => Promise<void>;
}) {
  const [email, setEmail] = useState("");
  const [stationIds, setStationIds] = useState<string[]>(
    editor.mode === "edit"
      ? editor.stationAdmin.stations.map((station) => station.id)
      : [],
  );
  const hasValidSelection = stationIds.length > 0;
  const hasValidEmail = editor.mode === "edit" || email.trim().length > 0;
  const canSubmit = hasValidSelection && hasValidEmail;

  const toggleStation = (stationId: string) => {
    setStationIds((currentStationIds) =>
      currentStationIds.includes(stationId)
        ? currentStationIds.filter((id) => id !== stationId)
        : [...currentStationIds, stationId],
    );
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) {
      return;
    }
    void onSubmit({ email: email.trim(), stationIds });
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      isCentered
      closeOnOverlayClick={!isSubmitting}
    >
      <ModalOverlay />
      <ModalContent mx="4">
        <form onSubmit={handleSubmit}>
          <ModalHeader>
            {editor.mode === "create"
              ? "Stationsadmin hinzufügen"
              : "Stationszugriff bearbeiten"}
          </ModalHeader>
          <ModalCloseButton isDisabled={isSubmitting} />
          <ModalBody>
            <Stack spacing="6">
              {editor.mode === "create" ? (
                <FormControl isRequired>
                  <FormLabel>E-Mail des bestehenden Kontos</FormLabel>
                  <Input
                    type="email"
                    value={email}
                    autoFocus
                    autoComplete="email"
                    placeholder="name@organisation.de"
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </FormControl>
              ) : (
                <Box bg="gray.50" borderRadius="md" p="4">
                  <Text color="gray.500" fontSize="sm">
                    Konto
                  </Text>
                  <Text fontWeight="semibold" overflowWrap="anywhere">
                    {editor.stationAdmin.email}
                  </Text>
                </Box>
              )}

              <Box as="fieldset" minW="0" p="0" border="0">
                <Text as="legend" fontWeight="semibold">
                  Stationen auswählen
                </Text>
                <Text color="gray.500" fontSize="sm" mt="1">
                  Mindestens eine Station ist erforderlich.
                </Text>
                <Stack spacing="3" mt="4">
                  {stations.map((station) => (
                    <Checkbox
                      key={station.id}
                      isChecked={stationIds.includes(station.id)}
                      colorScheme="blue"
                      onChange={() => toggleStation(station.id)}
                    >
                      <Box>
                        <Text fontWeight="medium">{station.name}</Text>
                        <Text color="gray.500" fontSize="sm">
                          {station.slug}
                        </Text>
                      </Box>
                    </Checkbox>
                  ))}
                </Stack>
              </Box>

              {error && (
                <Alert status="error" role="alert">
                  {error}
                </Alert>
              )}
            </Stack>
          </ModalBody>
          <ModalFooter gap="3">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              isDisabled={isSubmitting}
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              colorScheme="blue"
              isDisabled={!canSubmit}
              isLoading={isSubmitting}
              loadingText="Speichert …"
            >
              {editor.mode === "create"
                ? "Admin ernennen"
                : "Zugriff speichern"}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}

function ConfirmDemotionModal({
  stationAdmin,
  isSubmitting,
  error,
  onClose,
  onConfirm,
}: {
  stationAdmin: StationAdmin;
  isSubmitting: boolean;
  error?: string;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}) {
  return (
    <Modal
      isOpen
      onClose={onClose}
      isCentered
      closeOnOverlayClick={!isSubmitting}
    >
      <ModalOverlay />
      <ModalContent mx="4">
        <ModalHeader>Adminrolle entfernen?</ModalHeader>
        <ModalCloseButton isDisabled={isSubmitting} />
        <ModalBody>
          <Stack spacing="4">
            <Text color="gray.600">
              <Box as="strong" color="gray.900">
                {stationAdmin.email}
              </Box>{" "}
              verliert den Zugriff auf alle zugewiesenen Stationen und Exporte.
            </Text>
            {error && (
              <Alert status="error" role="alert">
                {error}
              </Alert>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter gap="3">
          <Button variant="ghost" onClick={onClose} isDisabled={isSubmitting}>
            Abbrechen
          </Button>
          <Button
            colorScheme="red"
            isLoading={isSubmitting}
            loadingText="Entfernt …"
            onClick={() => void onConfirm()}
          >
            Adminrolle entfernen
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
